import { useEffect, useState } from "react";
import { MailCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { useTheme } from "@/lib/ThemeContext";
import { api } from "@/lib/api";

const PROBE_DOMAINS = ["mmntm.test", "example.test", "example.com", "test.com"];
const KEY = "mmntm:verify-banner-dismissed";

// Bundle E-4: calm, dismissible-per-session nudge for unverified accounts. Hidden for the owner and probe domains.
export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const { tokens: t } = useTheme();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(KEY) === "1");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user?.email_verified) sessionStorage.removeItem(KEY); }, [user?.email_verified]);
  const domain = (user?.email || "").split("@")[1] || "";
  if (!user || user.email_verified !== false || user.isShadowOwner || dismissed || PROBE_DOMAINS.includes(domain)) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const res = await api.resendVerification();
      toast.success(res.status === "already_verified" ? "Your email is already verified" : "Verification link sent — check your inbox");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the link just now");
    }
    setBusy(false);
  };
  const dismiss = () => { sessionStorage.setItem(KEY, "1"); setDismissed(true); };

  return (
    <div className="flex items-center gap-3 px-4 py-2 text-xs" style={{ background: t.bgSecondary, borderBottom: `1px solid ${t.borderColor}`, color: t.textMuted }} data-testid="verify-email-banner">
      <MailCheck className="h-3.5 w-3.5 shrink-0" style={{ color: t.accent }} />
      <span className="flex-1 min-w-0">Verify your email so you can recover your account.</span>
      <button type="button" onClick={resend} disabled={busy} className="font-semibold hover:underline disabled:opacity-50 shrink-0" style={{ color: t.accent }} data-testid="verify-email-resend">
        {busy ? "Sending…" : "Resend link"}
      </button>
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="p-0.5 rounded shrink-0" style={{ color: t.textMuted }} data-testid="verify-email-dismiss"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}
